/**
 * Détection d'une nouvelle version de la plateforme pendant qu'une page reste ouverte.
 *
 * - Version de CETTE page : `<meta name="app-version">` posée dans `index.html` par le build
 *   (absente en développement : aucune détection).
 * - Version SERVIE : en-tête `X-App-Version` de chaque réponse `/api/…`, lu par `services/api/client.ts`,
 *   et sondage léger de `GET /api/version` (un élève qui écrit du code ne fait aucun appel pendant
 *   des minutes).
 * Différentes : le bandeau propose de recharger. Rien n'est jamais rechargé de force.
 */

export const APP_VERSION_HEADER = 'X-App-Version';
export const APP_VERSION_META = 'app-version';

/** Pages où l'on n'invite jamais à recharger : examen blanc et Entraînement IA. */
const QUIET_PATH_PREFIXES = ['/certification', '/ai-training'];

export function readBuiltVersion(doc: Pick<Document, 'querySelector'> | undefined = typeof document !== 'undefined' ? document : undefined): string | null {
  const content = doc?.querySelector(`meta[name="${APP_VERSION_META}"]`)?.getAttribute('content')?.trim();
  return content ? content : null;
}

/** Vrai quand la page tourne sur un autre build que celui que le serveur sert. */
export function isNewVersionAvailable(built: string | null, served: string | null): boolean {
  return Boolean(built && served && built !== served);
}

export function isQuietPath(pathname: string): boolean {
  return QUIET_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'));
}

export interface BannerInput {
  built: string | null;
  served: string | null;
  /** Version pour laquelle l'élève a fermé le bandeau, s'il l'a fermé. */
  dismissedFor: string | null;
  pathname: string;
}

export function shouldShowVersionBanner({ built, served, dismissedFor, pathname }: BannerInput): boolean {
  if (!isNewVersionAvailable(built, served)) return false;
  if (dismissedFor !== null && dismissedFor === served) return false;
  return !isQuietPath(pathname);
}

// --- Version servie, partagée entre le client HTTP (qui l'écrit) et le bandeau (qui la lit) ---

let servedVersion: string | null = null;
const listeners = new Set<() => void>();

export function getServedVersion(): string | null {
  return servedVersion;
}

/** Appelé à chaque réponse de l'API ; une valeur absente ou vide ne change rien. */
export function noteServedVersion(value: string | null | undefined): void {
  const version = value?.trim();
  if (!version || version === servedVersion) return;
  servedVersion = version;
  listeners.forEach((listener) => listener());
}

export function subscribeServedVersion(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Remet la version servie à zéro (tests). */
export function resetServedVersion(): void {
  servedVersion = null;
  listeners.forEach((listener) => listener());
}
